import { useState, useCallback, useEffect, useRef } from "react";
import bodycamClient from "../../../api/clients/bodycamClient";
import { Dayjs } from "dayjs";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";
import { EvidenceFileDDB } from "../../../shared/interfaces/EvidenceFileDDB";
import { EvidenceListResponse } from "../../../shared/interfaces/EvidenceListResponse";
import { VideoStatus } from "../../../shared/interfaces/VideoData";
import { useToast } from "../../../shared/toast";
import { isPlausibleRecordedAtMs } from "../utils/fileMetadata";

export const getDisplayRecordedAtMs = (evidence: EvidenceFileDDB): number =>
  isPlausibleRecordedAtMs(evidence.recordedAt)
    ? evidence.recordedAt
    : evidence.createdAt;

// Helper to map API response to VideoPreview
const mapEvidenceToVideoPreview = (evidence: EvidenceFileDDB): VideoPreview => ({
  id: evidence.evidenceId,
  thumbnailUrl: evidence.file, // Using file URL as thumbnail
  rmsName: evidence.externalId || "N/A",
  fileName: evidence.fileName,
  recordedDate: new Date(getDisplayRecordedAtMs(evidence)).toString(),
  uploadedDate: new Date(evidence.createdAt).toString(),
  officerName: evidence.uploadedByUserId || "System",
  duration: evidence.duration,
  status: evidence.status as unknown as VideoStatus,
  device: evidence.device || "Bodycam",
  fileType: evidence.fileType,
});

export const useListVideos = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [videos, setVideos] = useState<VideoPreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bodycamVendor, setBodycamVendor] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination state
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);

  // Ref to cancel in-flight requests when a newer one starts
  const abortControllerRef = useRef<AbortController | null>(null);

  // States for date range filter
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  // State for sorting
  const [sortBy, setSortBy] = useState<"createdAt" | "recordedAt">("createdAt");

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toast = useToast();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  /**
   * Fetches videos from the API.
   * @param token - The pagination token (optional)
   * @param mode  - "replace": swap the whole list (initial / filter change)
   *               "append":  add next page to the end (loadMore)
   *               "merge":   update statuses in-place and prepend new items,
   *                          preserving the pagination cursor (polling)
   */
  const fetchVideos = useCallback((token?: string, mode: "replace" | "append" | "merge" = "replace") => {
    // Cancel any in-flight request so stale responses are ignored
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    const params: any = {
      nextToken: token,
      sortBy: sortBy,
    };

    if (dateFrom) {
      params.dateFrom = dateFrom.startOf('day').toISOString();
    }

    if (dateTo) {
      params.dateTo = dateTo.endOf('day').toISOString();
    }

    if (debouncedSearchTerm) {
      params.textQuery = debouncedSearchTerm;
    }

    bodycamClient
      .get<EvidenceListResponse>("/evidence", { params, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;

        const data = response.data;
        const newVideos = (data.evidence || []).map(mapEvidenceToVideoPreview);

        if (mode === "replace") {
          setVideos(newVideos);
          setNextToken(data.nextToken);
          setHasMore(!!data.nextToken);
        } else if (mode === "append") {
          setVideos((prev) => [...prev, ...newVideos]);
          setNextToken(data.nextToken);
          setHasMore(!!data.nextToken);
        } else {
          // merge: update statuses in-place for known ids, prepend genuinely new items.
          // nextToken/hasMore are intentionally left unchanged so the pagination
          // cursor for loaded pages is not overwritten by a page-1 poll.
          setVideos((prev) => {
            const prevIds = new Set(prev.map((v) => v.id));
            const toAdd = newVideos.filter((v) => !prevIds.has(v.id));
            const merged = prev.map((existing) => {
              const fresh = newVideos.find((v) => v.id === existing.id);
              return fresh ? { ...existing, status: fresh.status } : existing;
            });
            return toAdd.length ? [...toAdd, ...merged] : merged;
          });
        }

        if (data.bodycamVendor) {
          setBodycamVendor(data.bodycamVendor);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        toast.error(error.response?.data?.error || "Failed to fetch videos");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      });
  }, [dateFrom, dateTo, debouncedSearchTerm, sortBy, toast]);

  /**
   * Loads the next page of videos if available.
   */
  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchVideos(nextToken, "append");
    }
  }, [hasMore, isLoading, fetchVideos, nextToken]);

  // Initial load and re-fetch when date filters change
  useEffect(() => {
    setNextToken(undefined);
    setHasMore(false);
    fetchVideos(undefined, "replace");
    return () => { abortControllerRef.current?.abort(); };
  }, [dateFrom, dateTo, debouncedSearchTerm, sortBy, fetchVideos]);

  // Poll every 10 seconds using merge mode so appended pages are preserved
  // and the pagination cursor is not overwritten by a page-1 response.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVideos(undefined, "merge");
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchVideos]);

  return {
    videos,
    isLoading,
    isInitialLoad,
    bodycamVendor,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sortBy,
    setSortBy,
    fetchVideos: () => fetchVideos(undefined, "replace"),
    loadMore,
    hasMore,
    searchTerm,
    setSearchTerm,
    handleSearchChange
  };
};
